import { Delivery } from '~/models/user';
import moment from 'moment-timezone';
import { useAuth } from '~/providers/AuthProvider';
import { useMemo } from 'react';
import { Box, ThemeProvider, Typography } from '@mui/material';
import CalendarIcon from '~/assets/images/icons/calendar-fill.svg';
import Tooltip from '~/components/common/Tooltip';
import { isEmpty } from 'lodash';
import { formatSimpleTime } from '~/utils/common';

type Props = {
  data: Delivery;
  prefix?: string;
};

const formatTime = (timeStr: string) =>
  formatSimpleTime(moment(timeStr, 'HH:mm'));

const getTodaySource = (data: Delivery, timezone: string) => {
  const now = moment.tz(timezone);
  const todayWeekDay = now.format('dddd').toLowerCase();
  const todayDate = now.format('YYYY-MM-DD');

  const special = data?.special_hours.find((s) =>
    moment(todayDate).isBetween(s.start_date, s.end_date, undefined, '[]'),
  );

  return special ?? data.delivery_hours[todayWeekDay];
};

const getTodayShiftText = (
  data: Delivery,
  timezone: string,
  prefix: string,
): string => {
  if (isEmpty(data) || !data?.enable_delivery) return '';

  const source = getTodaySource(data, timezone);
  if (!source || !source.available) return 'Off shift';
  if (source.all_hour) return '24 hours';

  const shifts = source.shifts ?? [];
  if (shifts.length === 0) return 'Off shift';

  const now = moment.tz(timezone);
  const nowTime = moment.tz(now.format('HH:mm'), 'HH:mm', timezone);

  const currentShift = shifts.find(({ start_time, end_time }) => {
    const start = moment.tz(start_time, 'HH:mm', timezone);
    const end = moment.tz(end_time, 'HH:mm', timezone);
    if (end.isBefore(start)) end.add(1, 'day');
    return nowTime.isBetween(start, end);
  });

  const targetShift = currentShift ?? shifts[0];
  const shiftText = `${formatTime(
    targetShift.start_time,
  )} ${prefix} ${formatTime(targetShift.end_time)}`;

  return shifts.length > 1 ? `${shiftText}...` : shiftText;
};

const getAllShiftsText = (
  data: Delivery,
  timezone: string,
): React.ReactNode => {
  if (isEmpty(data) || !data?.enable_delivery) return '';
  const source = getTodaySource(data, timezone);
  if (!source || !source.available || source.all_hour) return '';

  const shifts = source.shifts ?? [];
  if (shifts.length <= 1) return '';

  const rows = [];
  for (let i = 0; i < shifts.length; i += 2) {
    const pair = shifts
      .slice(i, i + 2)
      .map(
        ({ start_time, end_time }) =>
          `${formatTime(start_time)} - ${formatTime(end_time)}`,
      );
    rows.push(pair.join(', '));
  }

  return (
    <Box>
      {rows.map((row) => (
        <Typography key={row}>{row}</Typography>
      ))}
    </Box>
  );
};

const DriverShifts = ({ data, prefix = '-' }: Props) => {
  const { account } = useAuth();
  const timezone =
    account.timezone === 'auto' ? moment.tz.guess() : account.timezone;

  const content = useMemo(
    () => getTodayShiftText(data, timezone, prefix),
    [data, timezone],
  );

  const tooltipContent = useMemo(
    () => getAllShiftsText(data, timezone),
    [data, timezone],
  );

  if (!data || !data?.enable_delivery) return null;
  return (
    <ThemeProvider
      theme={{
        components: {
          MuiTooltip: {
            styleOverrides: {
              tooltip: {
                fontSize: 14,
                backgroundColor: '#33363D',
                padding: 4,
                textAlign: 'left',
                borderRadius: '4px 6px',
                color: 'white',
              },
            },
          },
        },
      }}
    >
      <Tooltip title={tooltipContent} placement='top'>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 0.4,
            cursor: 'default',
            whiteSpace: 'nowrap',
            maxWidth: '164px',
          }}
        >
          <img src={CalendarIcon} alt='calendar-icon' />
          <Typography
            sx={{
              color: '#717D96',
            }}
          >
            {content}
          </Typography>
        </Box>
      </Tooltip>
    </ThemeProvider>
  );
};

export default DriverShifts;
